import { getApps, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signOut,
} from 'firebase/auth';

import { USER_TYPES } from '../Constant/Enums';

import {
  ADMIN_FIREBASE_CONFIG,
  PROFESSIONAL_FIREBASE_CONFIG,
  USER_FIREBASE_CONFIG,
} from './config';

const FIREBASE_APP_CONFIGS = {
  [USER_TYPES.USER]: {
    appName: 'evento-user-client',
    config: USER_FIREBASE_CONFIG,
  },
  [USER_TYPES.PROFESSIONAL]: {
    appName: 'evento-professional-client',
    config: PROFESSIONAL_FIREBASE_CONFIG,
  },
  [USER_TYPES.ADMIN]: {
    appName: 'evento-admin-client',
    config: ADMIN_FIREBASE_CONFIG,
  },
};

const getOrCreateFirebaseApp = ({ appName, config }) => {
  const existingApp = getApps().find(app => app.name === appName);
  if (existingApp) return existingApp;

  return initializeApp(config, appName);
};

// Firebase Auth instances are created LAZILY, one per user type, only when a
// caller first asks for that type. Previously all three (user / professional /
// admin) were initialised eagerly at module load, so EVERY tab spun up all
// three Auth instances and App.js subscribed to all three. Running several Auth
// instances of different Firebase projects against the shared
// browserLocalPersistence is what let one tab disturb another's session — e.g.
// reloading the professional tab re-initialised the admin Auth instance and
// signed the admin tab out. With lazy init a tab only ever touches the Firebase
// app matching its own session type, so the identities stay fully isolated.
const authByUserType = {};
const authSetupByUserType = {};
const authReadyByUserType = {};

const initAuth = userType => {
  const appConfig = FIREBASE_APP_CONFIGS[userType];
  if (!appConfig) return null;

  if (!authByUserType[userType]) {
    const auth = getAuth(getOrCreateFirebaseApp(appConfig));
    authByUserType[userType] = auth;

    authSetupByUserType[userType] = setPersistence(auth, browserLocalPersistence)
      .catch(() => undefined)
      .then(() => auth);

    authReadyByUserType[userType] = authSetupByUserType[userType].then(
      () =>
        new Promise(resolve => {
          let unsubscribe = () => undefined;
          unsubscribe = onAuthStateChanged(auth, () => {
            unsubscribe();
            resolve(auth);
          });
        })
    );
  }

  return authByUserType[userType];
};

export const getFirebaseAuthByUserType = userType => initAuth(userType);

export const prepareFirebaseAuth = userType => {
  initAuth(userType);
  return authSetupByUserType[userType] || Promise.resolve(null);
};

export const waitForFirebaseAuth = userType => {
  initAuth(userType);
  return authReadyByUserType[userType] || Promise.resolve(null);
};

export const observeFirebaseAuth = (userType, callback) => {
  const auth = initAuth(userType);
  if (!auth) return () => undefined;

  return onAuthStateChanged(auth, callback);
};

export const signOutFirebaseUser = userType => {
  const auth = initAuth(userType);
  if (!auth) return Promise.resolve();

  return signOut(auth);
};
